import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";

interface Customer {
  id: number;
  firstName: string;
  lastName: string;
}

type DeleteCustomerPopupProps = {
  isVisible: boolean;
  onClose: () => void;
}

const DeleteCustomerPopup: React.FC<DeleteCustomerPopupProps> = ({ isVisible, onClose }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchCustomers = async () => {
    try {
      const response = await fetch("https://lscinvoice.com/api/get-customers/");
      if (!response.ok) throw new Error("Failed to fetch customers");
      const data: Customer[] = await response.json();
      setCustomers(data);
      if (data.length > 0) {
        setSelectedCustomerId(data[0].id);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      setMessage("Failed to load customers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVisible) {
      setLoading(true);
      fetchCustomers();
    }
  }, [isVisible]);

  const handleDelete = async () => {
    if (!selectedCustomerId) {
      setMessage("Please select a customer to delete.");
      return;
    }

    const customer = customers.find((c) => c.id === selectedCustomerId);
    const customerName = customer ? customer.firstName : "Unknown";

    setLoading(true);
    setMessage(`Deleting ${customerName}...`);

    try {
      const response = await fetch(`https://lscinvoice.com/api/delete-customer/${selectedCustomerId}`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error("Failed to delete customer");

      setCustomers(customers.filter((c) => c.id !== selectedCustomerId));
      setMessage(`${customerName} deleted successfully.`);

      const remaining = customers.filter((c) => c.id !== selectedCustomerId);
      setSelectedCustomerId(remaining.length > 0 ? remaining[0].id : null);
    } catch (err) {
      console.error(err);
      setMessage(`Error deleting ${customerName}.`);
    } finally {
      setLoading(false);
    }
    onClose();
  };

  const handleCancel = () => {
    setMessage("");
    onClose();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="confirm-backdrop"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="confirm-box"
          >
            <h3>Delete Customer</h3>

            {loading ? (
              <div>Loading customers...</div>
            ) : customers.length > 0 ? (
              <div className="flex">
                <label htmlFor="customer-select">
                  Select a customer to delete:
                </label>
                <select
                  id="customer-select"
                  value={selectedCustomerId ?? ""}
                  onChange={(e) => setSelectedCustomerId(Number(e.target.value))}
                >
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.id} {c.firstName} {c.lastName}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>No customers to delete.</div>
            )}

            <p>{message}</p>

            <div className="flex">
              <button
                onClick={handleDelete}
                disabled={!selectedCustomerId || loading}
                className="delete-button"
              >
                Delete
              </button>
              <button
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DeleteCustomerPopup;
